import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, HelpCircle, Search } from "lucide-react";

const fallbackFaqs = [
  {
    _id: "fallback-1",
    category: "General",
    question: "What is Refine Education?",
    answer:
      "Refine Education is LWBL's learning area for practical guidance, creative development, and member resources.",
  },
  {
    _id: "fallback-2",
    category: "Access",
    question: "Who can view Refine Education materials?",
    answer:
      "Members with the required membership access can view education materials after signing in.",
  },
];

const RefineEducationFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadFaqs = async () => {
      try {
        const { data } = await axios.get("/api/refine-education");
        if (mounted) {
          const items = data.success && data.faqs?.length ? data.faqs : fallbackFaqs;
          setFaqs(items);
          setOpenId(items[0]?._id || null);
        }
      } catch (error) {
        if (mounted) {
          setFaqs(fallbackFaqs);
          setOpenId(fallbackFaqs[0]._id);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadFaqs();
    return () => {
      mounted = false;
    };
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(faqs.map((faq) => faq.category || "General")))],
    [faqs]
  );

  const filteredFaqs = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return faqs.filter((faq) => {
      const categoryMatch = activeCategory === "All" || (faq.category || "General") === activeCategory;
      const searchMatch =
        !normalized ||
        faq.question?.toLowerCase().includes(normalized) ||
        faq.answer?.toLowerCase().includes(normalized) ||
        faq.category?.toLowerCase().includes(normalized);
      return categoryMatch && searchMatch;
    });
  }, [activeCategory, faqs, query]);

  return (
    <section className="main-lwbl bg-[#F9FAFB] px-4 py-16 rounded-[30px]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
          <div>
            <p className="font-outfit text-xs font-semibold uppercase tracking-[0.28em] text-[#008EA8]">
              Refine Education
            </p>
            <h2 className="mt-3 font-cinzel text-3xl text-[#111718] md:text-5xl">
              Learning Guide
            </h2>
            <p className="mt-4 max-w-2xl font-outfit text-sm leading-7 text-[#5f6c6d]">
              Clear answers for accessing learning materials, understanding member resources, and making the most of Refine Education.
            </p>
          </div>

          <div className="rounded-2xl border border-[#dce5e5] bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3 rounded-xl border border-[#dce5e5] bg-[#f7f8f8] px-4 py-3">
              <Search size={18} className="text-[#008EA8]" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-full bg-transparent font-outfit text-sm text-[#111718] outline-none"
                placeholder="Search questions..."
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-full px-4 py-2 font-outfit text-xs font-semibold transition ${
                    activeCategory === category
                      ? "bg-[#111718] text-white"
                      : "border border-[#dce5e5] bg-white text-[#5f6c6d] hover:border-[#00A6C8]"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          {loading ? (
            [0, 1, 2].map((item) => (
              <div key={item} className="h-24 animate-pulse rounded-2xl border border-[#dce5e5] bg-white" />
            ))
          ) : filteredFaqs.length ? (
            filteredFaqs.map((faq) => {
              const isOpen = openId === faq._id;
              return (
                <article key={faq._id} className="overflow-hidden rounded-2xl border border-[#dce5e5] bg-white shadow-sm">
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : faq._id)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left"
                  >
                    <span className="flex min-w-0 items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#E8FBFD] text-[#008EA8]">
                        <HelpCircle size={18} />
                      </span>
                      <span>
                        <span className="mb-1 block font-outfit text-xs font-semibold uppercase tracking-[0.18em] text-[#008EA8]">
                          {faq.category || "General"}
                        </span>
                        <span className="block font-cinzel text-xl text-[#111718]">{faq.question}</span>
                      </span>
                    </span>
                    <ChevronDown
                      size={20}
                      className={`flex-shrink-0 text-[#008EA8] transition ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#e8eeee] px-5 pb-5 pt-4">
                      <p className="whitespace-pre-wrap font-outfit text-sm leading-7 text-[#4c5a5b]">{faq.answer}</p>
                    </div>
                  )}
                </article>
              );
            })
          ) : (
            <div className="rounded-2xl border border-dashed border-[#c9d2d2] bg-white p-10 text-center">
              <h3 className="font-cinzel text-2xl text-[#111718]">No matching questions</h3>
              <p className="mt-2 font-outfit text-[#687475]">Try a different search or category.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RefineEducationFAQ;
